import { useState } from 'react';
import { Alert, Box, Button, Checkbox, Modal, SpaceBetween } from '@amzn/awsui-components-react';
import type { DismissalFailure } from '../useDismissal';
import { ABSENT_MARK } from './scale';

/**
 * `Ignore` on one opportunity: pick which of its recommended assets are irrelevant.
 *
 * The boxes show the CURRENT state — an already-dismissed recommendation starts ticked — so the
 * same dialog both ignores and undoes. Only a changed box is written, one call per (opportunity,
 * asset) pair, because that is the grain `PutOppAssetDismissal` / `DeleteOppAssetDismissal` take
 * and there is no batch op.
 *
 * After a failure only a `failed` pair is sent again. A `denied` one is a missing role and a
 * `not-judged` one a recommendation the server no longer has — resending either cannot succeed
 * (see `DismissalFailureKind`), so their messages say what to do instead and Confirm skips them.
 */

const retryable = (f: DismissalFailure | undefined) => f === undefined || f.kind === 'failed';

export interface IgnoreCandidate {
  assetId: string;
  name: string;
  score: number;
  dismissed: boolean;
}

/** One pair to write: `dismissed: true` ignores it, `false` undoes an earlier ignore. */
export interface DismissalChange {
  assetId: string;
  dismissed: boolean;
}

export interface IgnoreAssetsModalProps {
  /** Heads the dialog, so the user can see which row they opened it from. */
  oppName: string;
  candidates: IgnoreCandidate[];
  /**
   * Sends the changes and resolves to the ones that failed, keyed by asset. The dialog closes on
   * an empty map; otherwise it stays open on the failures, with every box where the user left it.
   */
  onSubmit: (changes: DismissalChange[]) => Promise<ReadonlyMap<string, DismissalFailure>>;
  onClose: () => void;
}

export function IgnoreAssetsModal({
  oppName,
  candidates,
  onSubmit,
  onClose,
}: IgnoreAssetsModalProps) {
  const [ticked, setTicked] = useState<ReadonlySet<string>>(
    () => new Set(candidates.filter((c) => c.dismissed).map((c) => c.assetId)),
  );
  const [submitting, setSubmitting] = useState(false);
  const [failures, setFailures] = useState<ReadonlyMap<string, DismissalFailure>>(new Map());

  const changes: DismissalChange[] = candidates
    .filter((c) => ticked.has(c.assetId) !== c.dismissed)
    .filter((c) => retryable(failures.get(c.assetId)))
    .map((c) => ({ assetId: c.assetId, dismissed: ticked.has(c.assetId) }));

  const submit = async () => {
    setSubmitting(true);
    const failed = await onSubmit(changes);
    setSubmitting(false);
    // Pairs not sent this time keep the failure they already had.
    const sent = new Set(changes.map((c) => c.assetId));
    const kept = [...failures].filter(([assetId]) => !sent.has(assetId));
    const all = new Map([...kept, ...failed]);
    if (all.size === 0) onClose();
    else setFailures(all);
  };

  const nameOf = (assetId: string) =>
    candidates.find((c) => c.assetId === assetId)?.name ?? assetId;

  return (
    <Modal
      visible
      onDismiss={() => {
        if (!submitting) onClose();
      }}
      header="忽略推荐资产"
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onClose} disabled={submitting}>
              取消
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              loading={submitting}
              disabled={changes.length === 0}
            >
              确认
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <SpaceBetween size="m">
        <Box>
          <Box variant="span" fontWeight="bold">
            {oppName}
          </Box>
          <Box variant="span" color="text-body-secondary">
            {' '}
            · 勾选与该机会无关的资产；取消勾选即撤销忽略。
          </Box>
        </Box>
        <SpaceBetween size="xs">
          {candidates.map((c) => (
            <Checkbox
              key={c.assetId}
              checked={ticked.has(c.assetId)}
              // Fixed once it has failed for good: nothing the user ticks here can be sent.
              disabled={submitting || !retryable(failures.get(c.assetId))}
              onChange={({ detail }) =>
                setTicked((t) => {
                  const next = new Set(t);
                  if (detail.checked) next.add(c.assetId);
                  else next.delete(c.assetId);
                  return next;
                })
              }
            >
              {c.score.toFixed(1)} · {c.name || ABSENT_MARK}
            </Checkbox>
          ))}
        </SpaceBetween>
        {failures.size > 0 && (
          <Alert type="error" header="以下资产未能保存，其余已生效">
            <ul>
              {[...failures].map(([assetId, f]) => (
                <li key={assetId}>
                  {nameOf(assetId)}：{f.message}
                </li>
              ))}
            </ul>
          </Alert>
        )}
      </SpaceBetween>
    </Modal>
  );
}
